import { Box, Button, Flex, HStack, Link, Text, Icon, IconButton } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { LuHourglass, LuSun, LuMoon } from "react-icons/lu";
import { useLandingTheme } from "../Index";
import { useAnalytics } from "@/hooks/useAnalytics";

const MotionBox = motion.create(Box);

type NavbarProps = {
  forceSolidBackground?: boolean;
  onGetAccess?: () => void;
};

export const Navbar = ({ onGetAccess }: NavbarProps) => {
  const { theme, toggleTheme, isDark } = useLandingTheme();
  const TRACK = useAnalytics();

  const handleGetAccess = () => {
    TRACK.BUTTON_CLICKED({ type: "Get Early Access button - navbar" });

    if (onGetAccess) {
      onGetAccess();
    } else {
      const footer = document.querySelector("footer");
      footer?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={50}
      px={{ base: 4, md: 10 }}
      py={4}
      display="flex"
      justifyContent="center"
      pointerEvents="none"
    >
      <MotionBox
        as="nav"
        pointerEvents="auto"
        w="full"
        maxW="5xl"
        borderRadius="full"
        px={6}
        py={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        bg={theme.navBg}
        backdropFilter="blur(12px)"
        border="1px solid"
        borderColor={theme.border}
        boxShadow={isDark ? `0 0 0 1px ${theme.primary}1a` : "0 4px 20px rgba(0, 0, 0, 0.08)"}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Logo */}
        <Flex align="center" gap={3}>
          <Flex
            w={8}
            h={8}
            borderRadius="full"
            bg={`${theme.primary}1a`}
            align="center"
            justify="center"
            color={theme.primary}
          >
            <Icon as={LuHourglass} boxSize={5} />
          </Flex>
          <Text color={theme.textPrimary} fontSize="xl" fontWeight="bold" letterSpacing="tight">
            Muro
          </Text>
        </Flex>

        {/* Nav Links - Desktop */}
        <HStack gap={8} display={{ base: "none", md: "flex" }}>
          <Link
            href="#features"
            color={theme.textSecondary}
            fontSize="sm"
            fontWeight="medium"
            _hover={{ color: theme.primary, textDecoration: "none" }}
            transition="colors 0.2s"
          >
            Features
          </Link>
          <Link
            href="#how-it-works"
            color={theme.textSecondary}
            fontSize="sm"
            fontWeight="medium"
            _hover={{ color: theme.primary, textDecoration: "none" }}
            transition="colors 0.2s"
          >
            How it works
          </Link>
          <Link
            href="#faq"
            color={theme.textSecondary}
            fontSize="sm"
            fontWeight="medium"
            _hover={{ color: theme.primary, textDecoration: "none" }}
            transition="colors 0.2s"
          >
            FAQ
          </Link>
        </HStack>

        {/* Right side: Theme toggle + CTA */}
        <Flex align="center" gap={3}>
          {/* Theme Toggle */}
          <IconButton
            aria-label="Toggle theme"
            variant="ghost"
            size="sm"
            borderRadius="full"
            color={theme.textSecondary}
            _hover={{
              bg: `${theme.primary}15`,
              color: theme.primary,
            }}
            onClick={toggleTheme}
          >
            <Icon as={isDark ? LuSun : LuMoon} boxSize={5} />
          </IconButton>

          {/* CTA Button */}
          <Button
            bg={theme.primary}
            color="white"
            fontSize={{ base: "xs", md: "sm" }}
            fontWeight="bold"
            px={5}
            py={2.5}
            borderRadius="full"
            boxShadow={`0 4px 14px ${theme.primaryGlow}`}
            _hover={{
              bg: theme.primaryDark,
              boxShadow: `0 6px 20px ${theme.primaryGlow}`,
              transform: "translateY(-1px)",
            }}
            transition="all 0.3s"
            onClick={handleGetAccess}
          >
            Get Early Access
          </Button>
        </Flex>
      </MotionBox>
    </Box>
  );
};
